import clsx from 'clsx'
import { useEffect, useState } from 'react'
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form'
import { useQueryClient } from 'react-query'

import { Button } from '@wartek-id/button-v1'
import { Checkbox } from '@wartek-id/checkbox-v1'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Input } from '@wartek-id/input'
import { Select } from '@wartek-id/select'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast-v2'

import {
  API_PATH_PREDIKAT_KINERJA_ORGANISASI,
  useAPIUpdatePredikatKinerjaOrganisasi,
} from 'api/kinerja/useAPIKinerja'
import { ORGANIZATION_PREDICATE_RECOMMENDATION_RESULT_TEXT } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/predicateRecommendation'

import type { TPredicateRecommendationResult } from 'types/kinerja-pelaksanaan'

export type DialogUbahPredikatOrganisasiProps = {
  id: string
  initialValue: TPredicateRecommendationResult
  isOpen: boolean
  isSent: boolean
  npsn: string
  schoolName: string
  onClose?: () => void
}

type FormValues = {
  newPredikatKinerja: TPredicateRecommendationResult
  reason?: string
  confirmation?: string
}

export type DialogStep = 'INITIAL_CONFIRMATION' | 'FORM' | 'FINAL_CONFIRMATION'

export function DialogUbahPredikatOrganisasi({
  id,
  initialValue,
  isOpen,
  isSent,
  npsn,
  schoolName,
  onClose,
}: DialogUbahPredikatOrganisasiProps) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const [step, setStep] = useState<DialogStep>('INITIAL_CONFIRMATION')
  const formMethods = useForm<FormValues>({ mode: 'onChange' })

  const upsertMutation = useAPIUpdatePredikatKinerjaOrganisasi()

  const handleSubmit = (formValues: FormValues) => {
    upsertMutation.mutate(
      {
        tahapPredikatId: id,
        predicate: formValues.newPredikatKinerja,
        reason: formValues.reason ?? '',
        npsn,
      },
      {
        onError: () => {
          onClose?.()
          toast({
            message: 'Gagal ubah Predikat Kinerja Organisasi. Coba lagi.',
          })
        },
        onSuccess: () => {
          queryClient.invalidateQueries(API_PATH_PREDIKAT_KINERJA_ORGANISASI)

          onClose?.()
          toast({
            message: 'Berhasil ubah Predikat Kinerja Organisasi!',
            type: 'success',
          })
        },
      }
    )
  }

  // Reset step upon close
  useEffect(() => {
    if (!isOpen) {
      setStep('INITIAL_CONFIRMATION')
      formMethods.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  return (
    <Dialog
      isOpen={isOpen}
      closeOnOverlayClick={false}
      size="sm"
      onClose={onClose}
    >
      <DialogOverlay />
      <DialogContent>
        <FormProvider {...formMethods}>
          {step === 'INITIAL_CONFIRMATION' && (
            <StepInitialConfirmation
              isSent={isSent}
              schoolName={schoolName}
              onNext={() => setStep('FORM')}
              onBack={onClose}
            />
          )}

          {step === 'FORM' && (
            <StepForm
              schoolName={schoolName}
              initialValue={initialValue}
              isLoading={upsertMutation.isLoading}
              onSubmit={handleSubmit}
              onBack={onClose}
            />
          )}
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}

export const INITIAL_CONFIRMATION_CHECKBOX =
  'Saya mengerti dampak perubahan dan sudah diskusi dengan Kepala Sekolah.'

type StepInitialConfirmationProps = {
  isSent: boolean
  schoolName: string
  onBack: () => void
  onNext: () => void
}
function StepInitialConfirmation({
  isSent,
  schoolName,
  onBack,
  onNext,
}: StepInitialConfirmationProps) {
  const [isConfirmed, setConfirmed] = useState(false)

  const confirmationCheckbox = isSent
    ? 'Saya mengerti Penetapan Predikat Kinerja satuan pendidikan akan terdampak dan sudah diskusi dengan Kepala Sekolah.'
    : INITIAL_CONFIRMATION_CHECKBOX

  return (
    <div className="p-6 space-y-9">
      <div className="flex flex-col space-y-4">
        {isSent && (
          <Icon fontSize="lg" color="critical">
            warning
          </Icon>
        )}
        <Text variant="display-sm" as="h2">
          {isSent
            ? `Predikat Kinerja Organisasi sudah dikirim ke ${schoolName}`
            : 'Perubahan akan berdampak ke Penetapan Predikat Kinerja satuan pendidikan ini'}
        </Text>

        <Checkbox
          checked={isConfirmed}
          className="mb-4"
          labelPosition="start"
          onChange={() => setConfirmed(!isConfirmed)}
        >
          {confirmationCheckbox}
        </Checkbox>
      </div>
      <div className="flex space-x-3 !justify-start">
        <Button color="white" size="lg" onClick={onBack}>
          Kembali
        </Button>
        <Button
          color="blue"
          disabled={!isConfirmed}
          size="lg"
          type="button"
          onClick={() => onNext?.()}
        >
          Lanjut ubah
        </Button>
      </div>
    </div>
  )
}

type StepFormProps = {
  schoolName: string
  initialValue: TPredicateRecommendationResult
  isLoading: boolean
  onBack: () => void
  onSubmit: (params: FormValues) => void
}
function StepForm({
  initialValue,
  schoolName,
  isLoading,
  onSubmit,
  onBack,
}: StepFormProps) {
  const { control, register, formState, handleSubmit } = useFormContext()

  return (
    <>
      <DialogHeader title="Ubah Predikat Kinerja Organisasi" />
      <form className="p-6 space-y-4" onSubmit={handleSubmit(onSubmit)}>
        <PanelPredikatKinerjaOrganisasi
          state="INITIAL"
          value={initialValue}
          schoolName={schoolName}
        />
        <fieldset className="space-y-1 flex flex-col">
          <Text as="label" htmlFor="updated">
            Anda ingin ubah menjadi:
          </Text>
          <Controller
            control={control}
            rules={{ required: true }}
            name="newPredikatKinerja"
            render={({ field: { onChange, value } }) => (
              <Select
                className="whitespace-nowrap"
                id="updated"
                value={value}
                onChange={(e) => onChange(e.target.value)}
              >
                <option disabled className="text-disabled" selected value="">
                  Pilih di sini
                </option>
                {Object.entries(
                  ORGANIZATION_PREDICATE_RECOMMENDATION_RESULT_TEXT
                ).map(([value, label]) => (
                  <option value={value} key={value}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          />
        </fieldset>
        <fieldset className="space-y-1">
          <Text as="label" htmlFor="reason">
            Apa alasan Anda mengubah? (Opsional)
          </Text>
          <Input
            id="reason"
            placeholder="Ketik di sini"
            {...register('reason')}
          />
        </fieldset>

        <div className="flex space-x-3 justify-end">
          <Button color="white" size="lg" onClick={onBack}>
            Batal
          </Button>
          <Button
            color="blue"
            disabled={!formState.isValid || isLoading}
            size="lg"
            type="submit"
          >
            Simpan
          </Button>
        </div>
      </form>
    </>
  )
}

type PanelPredikatKinerjaOrganisasi = {
  schoolName: string
  state: 'INITIAL' | 'UPDATED'
  value: TPredicateRecommendationResult
}
function PanelPredikatKinerjaOrganisasi({
  schoolName,
  state,
  value,
}: PanelPredikatKinerjaOrganisasi) {
  const labelId =
    state === 'UPDATED' ? 'updated-value-label' : 'initial-value-label'

  return (
    <div className="space-y-1">
      <Text as="label" id={labelId}>
        {state === 'UPDATED'
          ? 'Anda ingin ubah menjadi:'
          : 'Predikat Kinerja Organisasi yang direkomendasikan:'}
      </Text>
      <div className="flex">
        <div className="w-full h-12 px-4 py-1 rounded-l border border-gray-70 flex items-center">
          <Text
            variant="heading-sm"
            aria-label={schoolName}
            className="overflow-wrap-anywhere line-clamp-2"
          >
            {schoolName}
          </Text>
        </div>
        <div
          className={clsx(
            'flex-shrink-0 h-12 w-44 px-6 py-1 flex items-center rounded-r min-w-0',
            state === 'INITIAL' ? 'bg-gray-90' : 'bg-blue-70'
          )}
        >
          <Text
            variant="heading-sm"
            color="inverse"
            className="overflow-wrap-anywhere line-clamp-2"
            aria-labelledby={labelId}
          >
            {ORGANIZATION_PREDICATE_RECOMMENDATION_RESULT_TEXT[value] ?? '-'}
          </Text>
        </div>
      </div>
    </div>
  )
}
